import { first, startCase, sumBy, uniqBy } from "lodash"
import { useRouter } from "@tanstack/react-router"
import {
  type MouseEvent,
  type RefObject,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react"
import {
  type ColDef,
  type DragStoppedEvent,
  type GridOptions,
  type ICellRendererParams,
  type IDoesFilterPassParams,
  type IRowNode,
  type RowSelectionOptions,
} from "ag-grid-community"
import { type AgGridReact, useGridFilter, type CustomFilterProps } from "ag-grid-react"

import { CustomerSalesTooltip } from "~/components/reusable/graphs/CustomerSalesGraph"
import { AggregationMethod, AggregationSelector } from "~/components/reusable/AggregationSelector"
import { useVariantMap } from "~/hooks/useVariants"
import { openProduct } from "~/utils/routerUtils"
import { useSkuStore } from "~/providers/sku-store-provider"
import { api, type RouterOutputs } from "~/utils/api"
import { PreorderGraphTooltip } from "~/components/reusable/graphs/PreorderGraph"
import { OrderFrequencyVarianceTooltip } from "~/components/reusable/graphs/OrderFrequencyVarianceGraph"
import CustomerColumnChooser, {
  CUSTOMER_TABLE_ALL_COLUMNS,
  CUSTOMER_TABLE_DEFAULT_COLUMNS,
} from "~/components/reusable/CustomerColumnChooser"
import { useRegisterTable, useTableHasConsumer } from "~/components/planning/TableRegistryContext"
import { useTableSearch } from "~/hooks/useTableSearch"
import { useDisabledColumnIdSet, useVisibleColumnIds } from "~/hooks/useDisabledColumns"
import { useProductTableAggregations, useProductTableDisplayColumns } from "~/hooks/useProductTable"
import { DataTableBaseV2 } from "./DataTableV2"
import { CellProductImage, OpenCellButton } from "../planning/ProductTable/ProductTableV2"
import { TagsCell } from "../planning/Tags/TagsCell"
import CustomAgGridTooltip from "./table/tooltips/CustomTooltip"
import { filterDefined, filterNotUndefinedOrNull, uniq } from "~/utils/tsUtils"
import { useAllStockOutTimes } from "~/hooks/useVariantTableHooks"
import { type IVariantSummary } from "~/hooks/useVariantTableData"
import { formatVariantId } from "~/utils/helpers"
import { formatNumber } from "~/utils/formatting"
import { useUser } from "~/providers/userProvider"
import { formatName } from "~/utils/namingUtils"
import {
  createGroupedStringColumn,
  createNumberColumn,
  createStringColumn,
  createSumColumn,
  createUniqueStringColumn,
} from "~/utils/columnTemplates"
import ExpandableSearchField from "~/components/planning/ExpandableSearchField"
import { filterOnSearchString } from "~/utils/search"
import { Badge } from "~/components/reusable/Badge"
import { Tooltip } from "~/components/reusable/Tooltip"
import HeaderCell from "~/components/reusable/table/headers/HeaderCell"
import {
  SelectedVariantsProvider,
  useSelectedVariantHandlers,
  useSelectedVariantHandlersOptional,
} from "~/context/SelectedVariantsProvider"
import Commandbar from "~/components/planning/Commandbar"
import { EditSkuTagsDialog } from "~/components/planning/Tags/EditSkuTagsDialog"

export enum CustomerTableColumn {
  CustomerType = "customerType",
  PreOrderHandling = "preOrderHandling",
}

type CustomerData = RouterOutputs["customer"]["getCustomerData"][0]

type ReorderLikelihood = "Overdue" | "High" | "Medium" | "Low"

const REORDER_LIKELIHOOD_SORT_WEIGHT: Record<ReorderLikelihood, number> = {
  Overdue: 1_000_000,
  High: 10_000,
  Medium: 100,
  Low: 1,
}

type GridContext = {
  likelihoodFilterRef: React.MutableRefObject<Set<ReorderLikelihood>>
  refreshAggregation: () => void
}

const ROW_SELECTION: RowSelectionOptions = {
  mode: "multiRow",
  groupSelects: "descendants",
  checkboxes: () => true,
  enableClickSelection: false,
}

const reorderLikelihoodBadgeVariant: Record<
  ReorderLikelihood,
  "red" | "green" | "yellow" | "gray"
> = {
  Overdue: "red",
  High: "green",
  Medium: "yellow",
  Low: "gray",
}

const DISPLAY_DAYS_CAP = 90

function formatDaysToReorder(days: number, cap = false): string {
  if (days > -1 && days < 1) return "0"
  if (days < 0) return `+${Math.abs(Math.round(days))}`
  if (cap && days > DISPLAY_DAYS_CAP) return `< -${DISPLAY_DAYS_CAP}`
  return `-${Math.round(days)}`
}

function computeDaysToReorder(
  daysSinceLastOrder: number,
  avgOrderFrequencyDays: number,
  avgOrderQuantity: number | null | undefined,
  totalPreorders: number | null | undefined,
  incomingPo: number | null | undefined,
): number {
  const dailyRunRate = avgOrderQuantity ? avgOrderQuantity / avgOrderFrequencyDays : 0
  const pipelineDays =
    dailyRunRate > 0 ? ((totalPreorders ?? 0) + (incomingPo ?? 0)) / dailyRunRate : 0
  return avgOrderFrequencyDays - daysSinceLastOrder + pipelineDays
}

function computeReorderLikelihood(
  daysSinceLastOrder: number | null | undefined,
  avgOrderFrequencyDays: number | null | undefined,
  avgOrderQuantity: number | null | undefined,
  totalPreorders: number | null | undefined,
  incomingPo: number | null | undefined,
  orderCount: number | null | undefined,
): ReorderLikelihood | null {
  if (!daysSinceLastOrder || !avgOrderFrequencyDays) return null
  if ((orderCount ?? 0) < 1) return null
  if (daysSinceLastOrder > avgOrderFrequencyDays * 3) return null
  const daysToReorder = computeDaysToReorder(
    daysSinceLastOrder,
    avgOrderFrequencyDays,
    avgOrderQuantity,
    totalPreorders,
    incomingPo,
  )
  const score = 1 - daysToReorder / avgOrderFrequencyDays
  if (score > 1.5) return "Overdue"
  if (score >= 0.75) return "High"
  if (score >= 0.4) return "Medium"
  return "Low"
}

function computeReorderLikelihoodScore(
  daysSinceLastOrder: number | null | undefined,
  avgOrderFrequencyDays: number | null | undefined,
  avgOrderQuantity: number | null | undefined,
  totalPreorders: number | null | undefined,
  incomingPo: number | null | undefined,
  orderCount: number | null | undefined,
): number | null {
  const level = computeReorderLikelihood(
    daysSinceLastOrder,
    avgOrderFrequencyDays,
    avgOrderQuantity,
    totalPreorders,
    incomingPo,
    orderCount,
  )
  if (!level) return null
  return { Low: 1, Medium: 2, High: 3, Overdue: 4 }[level]
}

function ReorderLikelihoodFilter({
  model,
  onModelChange,
  context,
}: CustomFilterProps<CustomerData, GridContext, { values: ReorderLikelihood[] } | null>) {
  const selected = new Set<ReorderLikelihood>(model?.values ?? [])

  const passesFilter = (data: CustomerData | null | undefined) => {
    const level = computeReorderLikelihood(
      data?.daysSinceLastOrder,
      data?.avgOrderFrequencyDays,
      data?.avgOrderQuantity,
      data?.totalPreorders,
      data?.incomingPo,
      data?.orderCount,
    )
    return level !== null && selected.has(level)
  }

  useGridFilter({
    doesFilterPass: (params: IDoesFilterPassParams<CustomerData>) => {
      if (params.node?.group) {
        return (params.node.allLeafChildren ?? []).some((leaf) => passesFilter(leaf.data))
      }
      return passesFilter(params.data)
    },
  })

  const { likelihoodFilterRef, refreshAggregation } = context

  const toggle = (level: ReorderLikelihood) => {
    const next = new Set(selected)
    if (next.has(level)) next.delete(level)
    else next.add(level)
    onModelChange(next.size > 0 ? { values: [...next] } : null)
    likelihoodFilterRef.current = next
    refreshAggregation()
  }

  return (
    <div className="p-3 flex flex-col gap-2">
      {(["Overdue", "High", "Medium", "Low"] as const).map((level) => (
        <label key={level} className="flex items-center gap-2 cursor-pointer">
          <input type="checkbox" checked={selected.has(level)} onChange={() => toggle(level)} />
          <Badge variant={reorderLikelihoodBadgeVariant[level]} size="sm">
            {level}
          </Badge>
        </label>
      ))}
    </div>
  )
}

function ReorderLikelihoodTooltipBody({
  level,
  daysSinceLastOrder,
  avgOrderFrequencyDays,
  avgOrderQuantity,
  totalPreorders,
  incomingPo,
  orderCount,
}: {
  level: ReorderLikelihood
  daysSinceLastOrder?: number | null
  avgOrderFrequencyDays?: number | null
  avgOrderQuantity?: number | null
  totalPreorders?: number | null
  incomingPo?: number | null
  orderCount?: number | null
}) {
  const description =
    level === "Overdue"
      ? "This customer's order is overdue. Consider following up."
      : level === "High"
        ? "We are expecting an order from this customer soon."
        : level === "Medium"
          ? "This customer is approaching their expected order window."
          : "We do not expect an order from this customer in the near future."

  const daysToReorder =
    daysSinceLastOrder && avgOrderFrequencyDays
      ? computeDaysToReorder(
          daysSinceLastOrder,
          avgOrderFrequencyDays,
          avgOrderQuantity,
          totalPreorders,
          incomingPo,
        )
      : null

  const daysText =
    daysToReorder !== null
      ? daysToReorder <= 0
        ? `Overdue by ${Math.abs(Math.round(daysToReorder))} days.`
        : `Estimated ~${Math.round(daysToReorder)} days until next order.`
      : null

  return (
    <div className="flex flex-col gap-1.5 text-left text-sm max-w-60">
      <Badge variant={reorderLikelihoodBadgeVariant[level]} size="sm">
        {level}
      </Badge>
      <p className="text-fg-muted">{description}</p>
      {daysSinceLastOrder && (
        <p className="text-fg-muted">
          Most recent order placed{" "}
          <span className="text-fg font-medium">{daysSinceLastOrder} days ago</span>.
        </p>
      )}
      {daysText && <p className="text-fg-muted">{daysText}</p>}
      {orderCount != null && (
        <p className="text-fg-muted text-xs">Based on {orderCount + 1} orders.</p>
      )}
    </div>
  )
}

function ReorderLikelihoodCell(params: ICellRendererParams<CustomerData>) {
  if (params.node.group) {
    const children = params.node.allLeafChildren ?? []
    const isFooter = params.node.footer

    if (isFooter) {
      // Footer: count totals per level
      const counts: Record<ReorderLikelihood, number> = { Overdue: 0, High: 0, Medium: 0, Low: 0 }
      children.forEach((child: { data?: CustomerData }) => {
        if (!child.data) return
        const level = computeReorderLikelihood(
          child.data.daysSinceLastOrder,
          child.data.avgOrderFrequencyDays,
          child.data.avgOrderQuantity,
          child.data.totalPreorders,
          child.data.incomingPo,
          child.data.orderCount,
        )
        if (level) counts[level]++
      })
      const entries = (["Overdue", "High", "Medium", "Low"] as const).filter((l) => counts[l] > 0)
      if (!entries.length) return <span className="text-fg-subtle">—</span>
      return (
        <div className="flex gap-1">
          {entries.map((level) => (
            <Badge key={level} variant={reorderLikelihoodBadgeVariant[level]} size="sm">
              {counts[level]}x {level}
            </Badge>
          ))}
        </div>
      )
    }

    type MostUrgent = { level: ReorderLikelihood; data: CustomerData; days: number }
    let mostUrgent: MostUrgent | null = null
    for (const child of children as Array<{ data?: CustomerData }>) {
      if (!child.data || !child.data.daysSinceLastOrder || !child.data.avgOrderFrequencyDays)
        continue
      const level = computeReorderLikelihood(
        child.data.daysSinceLastOrder,
        child.data.avgOrderFrequencyDays,
        child.data.avgOrderQuantity,
        child.data.totalPreorders,
        child.data.incomingPo,
        child.data.orderCount,
      )
      if (!level) continue
      const days = computeDaysToReorder(
        child.data.daysSinceLastOrder,
        child.data.avgOrderFrequencyDays,
        child.data.avgOrderQuantity,
        child.data.totalPreorders,
        child.data.incomingPo,
      )
      const score = REORDER_LIKELIHOOD_SORT_WEIGHT[level] - days
      if (
        !mostUrgent ||
        score > REORDER_LIKELIHOOD_SORT_WEIGHT[mostUrgent.level] - mostUrgent.days
      ) {
        mostUrgent = { level, data: child.data, days }
      }
    }

    if (!mostUrgent) {
      return (
        <Tooltip
          side="top"
          body={
            <p className="text-fg-muted text-sm max-w-48">
              No reorder likelihood available. Order history is insufficient across all variants.
            </p>
          }
        >
          <span className="text-fg-subtle">—</span>
        </Tooltip>
      )
    }

    const { level, data: rep, days } = mostUrgent
    return (
      <Tooltip
        side="top"
        body={
          <ReorderLikelihoodTooltipBody
            level={level}
            daysSinceLastOrder={rep.daysSinceLastOrder}
            avgOrderFrequencyDays={rep.avgOrderFrequencyDays}
            avgOrderQuantity={rep.avgOrderQuantity}
            totalPreorders={rep.totalPreorders}
            incomingPo={rep.incomingPo}
            orderCount={rep.orderCount}
          />
        }
      >
        <Badge variant={reorderLikelihoodBadgeVariant[level]} size="sm">
          {formatDaysToReorder(days, true)}
        </Badge>
      </Tooltip>
    )
  }
  const level = computeReorderLikelihood(
    params.data?.daysSinceLastOrder,
    params.data?.avgOrderFrequencyDays,
    params.data?.avgOrderQuantity,
    params.data?.totalPreorders,
    params.data?.incomingPo,
    params.data?.orderCount,
  )
  if (!level) {
    const reason = !params.data?.avgOrderFrequencyDays
      ? "No order frequency data available."
      : (params.data?.orderCount ?? 0) < 1
        ? "Insufficient order history. At least 2 orders are required."
        : "Customer appears inactive on this variant."
    return (
      <Tooltip side="top" body={<p className="text-fg-muted text-sm max-w-48">{reason}</p>}>
        <span className="text-fg-subtle">—</span>
      </Tooltip>
    )
  }
  const days =
    params.data?.daysSinceLastOrder && params.data?.avgOrderFrequencyDays
      ? computeDaysToReorder(
          params.data.daysSinceLastOrder,
          params.data.avgOrderFrequencyDays,
          params.data.avgOrderQuantity,
          params.data.totalPreorders,
          params.data.incomingPo,
        )
      : null
  return (
    <Tooltip
      side="top"
      body={
        <ReorderLikelihoodTooltipBody
          level={level}
          daysSinceLastOrder={params.data?.daysSinceLastOrder}
          avgOrderFrequencyDays={params.data?.avgOrderFrequencyDays}
          avgOrderQuantity={params.data?.avgOrderQuantity}
          totalPreorders={params.data?.totalPreorders}
          incomingPo={params.data?.incomingPo}
          orderCount={params.data?.orderCount}
        />
      }
    >
      <Badge variant={reorderLikelihoodBadgeVariant[level]} size="sm">
        {days !== null ? formatDaysToReorder(days, true) : level}
      </Badge>
    </Tooltip>
  )
}
ReorderLikelihoodCell.displayName = "ReorderLikelihoodCell"

const aggregationMethods = [
  AggregationMethod.Customer,
  AggregationMethod.ProductName,
  AggregationMethod.Variant,
  AggregationMethod.Country,
  AggregationMethod.City,
  AggregationMethod.ZipCode,
  AggregationMethod.CustomerType,
]

interface CustomerTableProps {
  collectionIds?: number[]
  variantIds?: string[]
  startDate?: Date
  endDate?: Date
  height?: string
  id?: string
}

export function CustomerTable(props: CustomerTableProps) {
  const gridRef = useRef<AgGridReact<CustomerData>>(null)
  const existing = useSelectedVariantHandlersOptional()

  if (existing) {
    return <CustomerTableInner {...props} gridRef={gridRef} />
  }

  return (
    <SelectedVariantsProvider<CustomerData> gridRef={gridRef}>
      <CustomerTableInner {...props} gridRef={gridRef} />
    </SelectedVariantsProvider>
  )
}

function CustomerTableInner({
  collectionIds,
  variantIds,
  startDate,
  endDate,
  height,
  id,
  gridRef,
}: CustomerTableProps & { gridRef: RefObject<AgGridReact<CustomerData> | null> }) {
  const tableId = id ?? "customerTable"

  const { getSelectedVariantIds, clearSelectedVariants } = useSelectedVariantHandlers()
  const [selectedVariantIds, setSelectedVariantIds] = useState<string[]>([])

  const handleSelectionChangedLocal = useCallback(() => {
    setSelectedVariantIds(getSelectedVariantIds())
  }, [getSelectedVariantIds])

  const handleClear = useCallback(() => {
    clearSelectedVariants()
    setSelectedVariantIds([])
  }, [clearSelectedVariants])

  const [displayedColumnIds, setDisplayedColumnIds] = useProductTableDisplayColumns(tableId, [
    ...CUSTOMER_TABLE_DEFAULT_COLUMNS,
  ])

  // Company-disabled columns are hidden from the grid even if still in the saved column list.
  const disabledColumnIdSet = useDisabledColumnIdSet()
  const visibleColumnIds = useVisibleColumnIds(displayedColumnIds)

  const displayedColumnIdsRef = useRef(displayedColumnIds)
  const setDisplayedColumnIdsRef = useRef(setDisplayedColumnIds)
  const disabledColumnIdSetRef = useRef(disabledColumnIdSet)
  useEffect(() => {
    displayedColumnIdsRef.current = displayedColumnIds
    setDisplayedColumnIdsRef.current = setDisplayedColumnIds
    disabledColumnIdSetRef.current = disabledColumnIdSet
  })

  const handleDragStopped = useCallback((event: DragStoppedEvent<CustomerData>) => {
    const toHide = event.api
      .getColumnState()
      .filter(
        (col) =>
          col.hide &&
          displayedColumnIdsRef.current.includes(col.colId) &&
          // Company-disabled columns are hidden by us, not the user — keep them in the saved
          // list so re-enabling them in settings restores them.
          !disabledColumnIdSetRef.current.has(col.colId),
      )
      .map((col) => col.colId)
    if (toHide.length === 0) return
    requestAnimationFrame(() => {
      setDisplayedColumnIdsRef.current(
        displayedColumnIdsRef.current.filter((id) => !toHide.includes(id)),
      )
    })
  }, [])

  const [aggregations, setAggregations] = useProductTableAggregations(
    tableId,
    AggregationMethod.Customer,
  )
  const { searchText, setSearchText } = useTableSearch(tableId)
  const likelihoodFilterRef = useRef<Set<ReorderLikelihood>>(new Set())

  const reorderLikelihoodComparator = useCallback(
    (
      _valueA: unknown,
      _valueB: unknown,
      nodeA: IRowNode<CustomerData>,
      nodeB: IRowNode<CustomerData>,
      isInverted: boolean,
    ) => {
      const computeBest = (node: IRowNode<CustomerData>) => {
        const activeFilter = likelihoodFilterRef.current
        const useAll = !activeFilter || activeFilter.size === 0
        let bestLevelWeight = -Infinity
        let bestDays = Infinity
        for (const leaf of node.allLeafChildren ?? [node]) {
          const level = computeReorderLikelihood(
            leaf.data?.daysSinceLastOrder,
            leaf.data?.avgOrderFrequencyDays,
            leaf.data?.avgOrderQuantity,
            leaf.data?.totalPreorders,
            leaf.data?.incomingPo,
            leaf.data?.orderCount,
          )
          if (!level || (!useAll && !activeFilter.has(level))) continue
          if (!leaf.data?.daysSinceLastOrder || !leaf.data?.avgOrderFrequencyDays) continue
          const levelWeight = REORDER_LIKELIHOOD_SORT_WEIGHT[level]
          const days = computeDaysToReorder(
            leaf.data.daysSinceLastOrder,
            leaf.data.avgOrderFrequencyDays,
            leaf.data.avgOrderQuantity,
            leaf.data.totalPreorders,
            leaf.data.incomingPo,
          )
          if (
            levelWeight > bestLevelWeight ||
            (levelWeight === bestLevelWeight && days < bestDays)
          ) {
            bestLevelWeight = levelWeight
            bestDays = days
          }
        }
        return bestLevelWeight === -Infinity
          ? null
          : { levelWeight: bestLevelWeight, days: bestDays }
      }
      const a = computeBest(nodeA)
      const b = computeBest(nodeB)

      if (!a && !b) return 0
      if (!a) return isInverted ? -1 : 1
      if (!b) return isInverted ? 1 : -1
      if (a.levelWeight !== b.levelWeight) return a.levelWeight - b.levelWeight
      return b.days - a.days
    },
    [],
  )

  const { permissions } = useUser()
  const availableCustomerColumnIds = useMemo(
    () =>
      permissions.isStaff
        ? CUSTOMER_TABLE_ALL_COLUMNS
        : CUSTOMER_TABLE_ALL_COLUMNS.filter((id) => id !== "preOrderHandling"),
    [permissions.isStaff],
  )
  useRegisterTable(tableId, {
    customerColumnChooserProps: {
      availableColumnIds: availableCustomerColumnIds,
      displayedColumnIds,
      setDisplayedColumnIds,
    },
    search: { placeholder: "Search customers, products..." },
  })
  const displayControlsHoisted = useTableHasConsumer(tableId)
  const selectedWarehouseId = useSkuStore((state) => state.warehouseId)
  const selectedChannel = useSkuStore((state) => state.channel)
  const router = useRouter()
  const variantMap = useVariantMap()

  const { data: stockOutData } = useAllStockOutTimes()

  const suggestedOrderMap = useMemo(() => {
    const map = new Map<string, number>()
    if (!stockOutData) return map
    for (const row of stockOutData.stockOutTimes.values()) {
      const variantId = row.aggregated.variantId
      const warehouseTotal = sumBy(Object.values(row.details), (d) => d.suggestedOrder ?? 0)
      map.set(variantId, (map.get(variantId) ?? 0) + warehouseTotal)
    }
    return map
  }, [stockOutData])

  const { data: customerData, isLoading } = api.customer.getCustomerData.useQuery({
    collectionIds,
    variantIds,
    warehouseId: selectedWarehouseId,
    channel: selectedChannel,
    startDate,
    endDate,
  })

  const enrichedCustomerData = useMemo(() => {
    if (!customerData) return []

    return customerData.map((row) => {
      const variant = variantMap.get(row.variantId ?? "")
      return {
        ...row,
        // Add variant/product fields for search
        // Note: Not sure we should add all these fields to the data. It's a lot of data to search through.
        variantName: variant?.variantName,
        productId: variant?.productId,
        productName: variant?.productName,
      }
    })
  }, [customerData, variantMap])

  // Supports: simple search, field-specific (e.g. "customer:acme"), and exact match (e.g. "productName")
  const filteredCustomerData = useMemo(() => {
    if (!searchText.trim()) return enrichedCustomerData
    return filterOnSearchString(enrichedCustomerData, searchText)
  }, [enrichedCustomerData, searchText])

  const openForecastView = useCallback(
    (variants: IVariantSummary[], newTab: boolean) => {
      const productId = variants[0]!.productId
      openProduct(
        productId,
        variants.map((v) => v.variantId),
        router,
        newTab,
      )
    },
    [router],
  )

  function primaryCellRenderer() {
    const PrimaryCellComponent = (params: any) => {
      const footer = params.node.footer

      if (footer) {
        return <span className="font-semibold">Totals</span>
      }

      const field = params.node.field

      const entries: CustomerData[] = filterDefined(
        params.node?.allLeafChildren?.map((n: any) => n.data),
      )

      const variant = variantMap.get(entries[0]?.variantId ?? "")
      const newImageUrl = variant?.imageUrl ?? ""

      if (field === "variantId" || field === "productId") {
        const productId = variant?.productId ?? "Unknown product"
        const variantId = formatVariantId(first(entries)?.variantId ?? "Unknown variant")
        const productName = variant?.productName ?? "Unknown product"
        const variantName = variant?.variantName ?? "Unknown variant"
        const imageUrl = variant?.imageUrl ?? ""

        const showBothNames =
          !aggregations?.includes(AggregationMethod.ProductName) &&
          !variantName.includes(productName)

        const title = showBothNames
          ? `${productName} / ${variantName}`
          : field === "variantId"
            ? variantName
            : productName

        return (
          <div className="relative flex flex-row items-center gap-4 h-[40px] w-[100%]">
            <CellProductImage imageUrl={imageUrl} />

            <div className="leading-5 min-w-0">
              <div className="label-sm truncate">{title}</div>
              <div className="text-fg-muted paragraph-sm truncate">
                #{field === "variantId" ? variantId : productId}
              </div>
            </div>
            <OpenCellButton onClick={createOnOpen(params)} />
          </div>
        )
      }

      return (
        <div className="flex items-center gap-3 leading-5 h-[40px]">
          <CellProductImage imageUrl={newImageUrl} />
          <span className="label-base">{params.value}</span>
        </div>
      )
    }

    PrimaryCellComponent.displayName = "PrimaryCellComponent"
    return PrimaryCellComponent
  }

  const createOnOpen =
    (params: any) =>
    (e: MouseEvent): void => {
      const newTab = e.metaKey || e.ctrlKey
      const leafNodes = params.node.allLeafChildren ?? [params.node]
      const entries = leafNodes
        ?.map((node: IRowNode<CustomerData>) => node.data)
        .filter(Boolean) as CustomerData[]

      const variants = filterNotUndefinedOrNull(
        entries.map((entry) => variantMap.get(entry.variantId ?? "")),
      )

      if (variants.length > 0) openForecastView(variants, newTab)
    }

  const handleAggregationChange = (aggregationMethod: AggregationMethod, level: number = 0) => {
    let aggregationsArray = [aggregationMethod]

    if (level === 1) {
      const newAggregations = [...aggregations]
      newAggregations[aggregations.length - 1] = aggregationMethod
      setAggregations(newAggregations)
      return
    }

    if (aggregationMethod === AggregationMethod.Customer) {
      aggregationsArray = [AggregationMethod.Customer, AggregationMethod.Variant]
    }

    switch (aggregationMethod) {
      case AggregationMethod.Customer:
        aggregationsArray = [AggregationMethod.Customer, AggregationMethod.Variant]
        break
      case AggregationMethod.Variant:
      case AggregationMethod.ProductName:
        aggregationsArray = [aggregationMethod, AggregationMethod.Customer]
        break
      case AggregationMethod.Country:
        aggregationsArray = [
          AggregationMethod.Country,
          AggregationMethod.Customer,
          AggregationMethod.Variant,
        ]
        break
      case AggregationMethod.City:
        aggregationsArray = [
          AggregationMethod.City,
          AggregationMethod.Customer,
          AggregationMethod.Variant,
        ]
        break
      case AggregationMethod.ZipCode:
        aggregationsArray = [
          AggregationMethod.ZipCode,
          AggregationMethod.Customer,
          AggregationMethod.Variant,
        ]
        break
    }

    setAggregations(aggregationsArray)
  }

  const autoGroupColumnDef: ColDef = useMemo(() => {
    return createGroupedStringColumn({
      minWidth: 300,
      field: "customerName",
      headerName: startCase(aggregations[0] ?? ""),
      cellRendererSelector: (params: any) => {
        if (params.node.uiLevel < aggregations.length - 1) {
          return {
            component: "agGroupCellRenderer",
            params: {
              suppressCount: true,
              innerRenderer: primaryCellRenderer(),
            },
          }
        } else {
          // This is done so that this row can't be expanded. Otherwise it would always expand to show the customer name.
          return {
            component: primaryCellRenderer(),
          }
        }
      },
    })
  }, [aggregations])

  const newColumns: ColDef<CustomerData>[] = useMemo(() => {
    // Customer rows only carry `variantId`; hydrate the full IVariantSummary (with tags +
    // skuIds) from the sku-store so the tags cell works on grouped rows too, mirroring
    // ProductTableV2's extractVariantSummaries.
    const resolveTagEntries = (params: {
      node?: IRowNode<CustomerData> | null
      data?: CustomerData | null
    }): IVariantSummary[] => {
      const rows = params.node?.group
        ? (params.node.allLeafChildren ?? []).map((leaf) => leaf.data)
        : [params.data]
      const variantIds = filterNotUndefinedOrNull(rows.map((row) => row?.variantId))
      return filterDefined(uniq(variantIds).map((id) => variantMap.get(id)))
    }

    const baseColumns: ColDef<CustomerData>[] = [
      createStringColumn({
        headerName: "Customer",
        field: "customerName",
        filter: true,
        hide: true,
      }),
      {
        headerName: "Reorder likelihood",
        headerComponentParams: {
          innerHeaderComponent: HeaderCell,
          subtitle: "days to next order",
        },
        colId: "reorderLikelihood",
        cellStyle: { display: "flex", alignItems: "center", overflow: "hidden" },
        valueGetter: (params) =>
          computeReorderLikelihoodScore(
            params.data?.daysSinceLastOrder,
            params.data?.avgOrderFrequencyDays,
            params.data?.avgOrderQuantity,
            params.data?.totalPreorders,
            params.data?.incomingPo,
            params.data?.orderCount,
          ),
        aggFunc: (params) => {
          const values = params.values.filter((v): v is number => typeof v === "number")
          if (!values.length) return null
          const activeFilter = (params.context as GridContext | null)?.likelihoodFilterRef?.current
          const useAll = !activeFilter || activeFilter.size === 0
          const overdue =
            useAll || activeFilter.has("Overdue") ? values.filter((v) => v === 4).length : 0
          const high = useAll || activeFilter.has("High") ? values.filter((v) => v === 3).length : 0
          const medium =
            useAll || activeFilter.has("Medium") ? values.filter((v) => v === 2).length : 0
          const low = useAll || activeFilter.has("Low") ? values.filter((v) => v === 1).length : 0
          return (
            overdue * REORDER_LIKELIHOOD_SORT_WEIGHT.Overdue +
            high * REORDER_LIKELIHOOD_SORT_WEIGHT.High +
            medium * REORDER_LIKELIHOOD_SORT_WEIGHT.Medium +
            low * REORDER_LIKELIHOOD_SORT_WEIGHT.Low
          )
        },
        comparator: reorderLikelihoodComparator,
        cellRenderer: ReorderLikelihoodCell,
        filter: ReorderLikelihoodFilter,
        sortable: true,
        minWidth: 200,
      } satisfies ColDef<CustomerData>,
      createSumColumn({
        headerName: formatName("sale", true, true),
        headerSubtitle:
          !startDate && !endDate
            ? collectionIds
              ? "Collection period"
              : "Last 52 weeks"
            : undefined,
        field: "lastYearSales",
        filter: true,
        alignment: "left",
        tooltipComponent: CustomerSalesTooltip,
        tooltip: (params: any) => {
          if (!params.value) return null
          return {
            variantIds: uniq(
              params.node?.allLeafChildren?.map((node: any) => node.data?.variantId ?? ""),
            ),
            customerIds: uniq(
              params.node?.allLeafChildren?.map((node: any) => node.data?.customerId ?? ""),
            ),
          }
        },
      }),
      createSumColumn({
        headerName: formatName("preorder", true, true),
        field: "totalPreorders",
        headerSubtitle:
          !startDate && !endDate
            ? collectionIds
              ? "Collection period"
              : "In the future"
            : undefined,
        filter: true,
        alignment: "left",
        tooltipComponent: PreorderGraphTooltip,
        tooltip: (params) => {
          if (!params.value) return null
          return {
            variantIds: uniq(
              params.node?.allLeafChildren?.map((node) => node.data?.variantId ?? ""),
            ),
            customerIds: uniq(
              params.node?.allLeafChildren?.map((node) => node.data?.customerId ?? ""),
            ),
          }
        },
      }),
      createSumColumn({
        headerName: "Incoming PO",
        field: "incomingPo",
        filter: true,
        alignment: "left",
      }),
      {
        headerName: "Suggested Order",
        colId: "suggestedOrder",
        valueGetter: (params) => {
          if (!params.data) return undefined
          return suggestedOrderMap.get(params.data.variantId ?? "") ?? 0
        },
        aggFunc: (params) => {
          const seen = new Set<string>()
          let total = 0
          for (const node of params.rowNode.allLeafChildren ?? []) {
            const variantId = node.data?.variantId
            if (variantId && !seen.has(variantId)) {
              seen.add(variantId)
              total += suggestedOrderMap.get(variantId) ?? 0
            }
          }
          return total
        },
        cellRenderer: (params: ICellRendererParams<CustomerData>) => {
          if (!params.node.group || params.node.uiLevel >= aggregations.length - 1) return "-"
          return params.value != null ? formatNumber(params.value, 0) : "-"
        },
      } satisfies ColDef<CustomerData>,
      createSumColumn({
        headerName: `${formatName("sale", false, true)} YTD`,
        field: "salesYtd",
        filter: true,
        alignment: "left",
      }),
      createSumColumn({
        headerName: `${formatName("sale", false, true)} same period LY`,
        field: "salesSamePeriodLastYear",
        filter: true,
        alignment: "left",
      }),
      createNumberColumn({
        headerName: "Days since last order",
        field: "daysSinceLastOrder",
        aggFunc: "min",
        filter: true,
        alignment: "left",
        cellRenderer: (params: ICellRendererParams<CustomerData>) => {
          const isCustomerLevel = params.node.childrenAfterGroup?.[0]?.group === true
          if (params.node.group && !params.node.footer && isCustomerLevel) {
            const values = ((params.node.allLeafChildren ?? []) as Array<{ data?: CustomerData }>)
              .map((leaf) => leaf.data?.daysSinceLastOrder)
              .filter((v): v is number => v != null)
            if (!values.length) return <span className="text-fg-subtle">—</span>
            const min = Math.min(...values)
            const max = Math.max(...values)
            if (min === max) return <span>{min}</span>
            return (
              <span>
                {min}
                <span className="text-fg-base opacity-90 mx-0.5">-</span>
                {max}
              </span>
            )
          }
          const val = params.node.group ? params.value : params.data?.daysSinceLastOrder
          return val != null ? <span>{val}</span> : <span className="text-fg-subtle">—</span>
        },
      }),
      createNumberColumn({
        headerName: "Avg order frequency",
        headerSubtitle: "days between orders",
        field: "avgOrderFrequencyDays",
        aggFunc: "avg",
        filter: true,
        alignment: "left",
        cellRenderer: (params: ICellRendererParams<CustomerData>) => {
          const isCustomerLevel = params.node.childrenAfterGroup?.[0]?.group === true
          if (params.node.group && !params.node.footer && isCustomerLevel) {
            const values = ((params.node.allLeafChildren ?? []) as Array<{ data?: CustomerData }>)
              .map((leaf) => leaf.data?.avgOrderFrequencyDays)
              .filter((v): v is number => v != null && v > 0)
            if (!values.length) return <span className="text-fg-subtle">—</span>
            const min = Math.min(...values)
            const max = Math.max(...values)
            if (min === max) return <span>{min}</span>
            return (
              <span>
                {min}
                <span className="text-fg-base opacity-90 mx-0.5">-</span>
                {max}
              </span>
            )
          }
          const val = params.node.group ? params.value : params.data?.avgOrderFrequencyDays
          return val != null ? (
            <span>{Math.round(val)}</span>
          ) : (
            <span className="text-fg-subtle">—</span>
          )
        },
      }),
      createNumberColumn({
        headerName: "Avg order quantity",
        headerSubtitle: "avg quantity per order",
        field: "avgOrderQuantity",
        aggFunc: "avg",
        filter: true,
        alignment: "left",
      }),
      createNumberColumn({
        headerName: "Order freq. variance",
        headerSubtitle: "avg deviation from order frequency (days)",
        field: "orderFrequencyVariance",
        aggFunc: "avg",
        filter: true,
        alignment: "left",
        tooltipComponent: OrderFrequencyVarianceTooltip,
        tooltip: (params) => {
          if (!params.value) return null
          return {
            variantIds: uniq(
              params.node?.allLeafChildren?.map((node) => node.data?.variantId ?? ""),
            ),
            customerIds: uniq(
              params.node?.allLeafChildren?.map((node) => node.data?.customerId ?? ""),
            ),
          }
        },
      }),
      createUniqueStringColumn({
        headerName: "Country",
        field: "country",
        filter: true,
        tooltipTitle: "Countries",
      }),
      createUniqueStringColumn({
        headerName: "City",
        field: "city",
        tooltipTitle: "Cities",
        filter: true,
      }),
      createUniqueStringColumn({
        headerName: "Zip Code",
        field: "zipCode",
        tooltipTitle: "Zip Codes",
        filter: true,
      }),
      createUniqueStringColumn({
        headerName: "Variant",
        field: "variantId",
        tooltipTitle: "Variants",
        hide: true,
      }),
      createUniqueStringColumn({
        headerName: "Product Name",
        field: "productId",
        tooltipTitle: "Products",
        hide: true,
      }),
      createUniqueStringColumn({
        headerName: "Customer ID",
        field: "customerId",
        tooltipTitle: "Customers",
      }),
      createUniqueStringColumn({
        headerName: "Customer type",
        colId: CustomerTableColumn.CustomerType,
        valueGetter: (params) => startCase(params.data?.customerType ?? ""),
        filter: true,
        tooltipTitle: "Customer types",
      }),
      createUniqueStringColumn({
        headerName: `${formatName("preorder", false, true)} handling`,
        colId: CustomerTableColumn.PreOrderHandling,
        valueGetter: (params) => startCase(params.data?.preOrderHandling ?? ""),
        filter: true,
        tooltipTitle: `${formatName("preorder", false, true)} handling`,
        hide: !permissions.isStaff,
      }),
      {
        headerName: "Tags",
        colId: "tags",
        valueGetter: (params) => {
          const entries = resolveTagEntries(params)
          if (!entries.length) return ""
          const tags = uniqBy(
            entries.flatMap((e) => e.tags),
            (t) => t.id,
          )
          return [...tags]
            .sort((a, b) => a.name.localeCompare(b.name))
            .map((t) => t.name)
            .join(", ")
        },
        cellRenderer: (params: ICellRendererParams<CustomerData>) => {
          const entries = resolveTagEntries(params)
          const value = uniqBy(
            entries.flatMap((entry) => entry.tags),
            (tag) => tag.id,
          )
          return <TagsCell entries={entries} value={value} node={params.node} />
        },
        tooltipComponent: CustomAgGridTooltip,
        tooltipComponentParams: { tooltipType: "Tags" },
        tooltip: (params) => {
          const entries = resolveTagEntries(params)
          const tags = uniqBy(
            entries.flatMap((entry) => entry.tags),
            (tag) => tag.id,
          )
          return { entries, tags }
        },
        autoHeight: true,
        minWidth: 175,
        sortable: true,
      } satisfies ColDef<CustomerData>,
    ]

    const finalAggregations = aggregations
      .map((agg) => startCase(agg.replace("(extradata)", "")))
      .map((agg) => agg.toString().toLowerCase())

    // eslint-disable-next-line react-hooks/refs -- comparator reads ref at sort time, not during render
    const mappedColDefs: ColDef<CustomerData>[] = baseColumns.map((colDef) => {
      const colId = colDef.colId ?? (colDef.field as string | undefined) ?? ""
      return {
        ...colDef,
        headerClass:
          // @ts-ignore
          colDef.cellStyle?.textAlign === "right" ? "ag-right-aligned-header" : "",
        rowGroup: finalAggregations.includes(colDef.headerName?.toLowerCase() ?? ""),
        rowGroupIndex: finalAggregations.indexOf(colDef.headerName?.toLowerCase() ?? ""),
        hide: colDef.hide === true ? true : !visibleColumnIds.includes(colId),
      }
    })

    return mappedColDefs
  }, [
    aggregations,
    permissions.isStaff,
    collectionIds,
    visibleColumnIds,
    suggestedOrderMap,
    variantMap,
  ])

  const gridOptions = useMemo<GridOptions<CustomerData>>(
    () => ({
      context: {
        likelihoodFilterRef,
        refreshAggregation: () => gridRef.current?.api.refreshClientSideRowModel("aggregate"),
      } satisfies GridContext,
      getRowId: (params) => `${params.data.customerId}_${params.data.variantId}`,
    }),
    [gridRef],
  )

  return (
    <>
      <div style={{ height: height ?? "100%" }} className="w-full flex flex-col gap-2">
        {!displayControlsHoisted && (
          <div className="flex flex-row items-center justify-between gap-2">
            <div className="flex flex-row items-center gap-2">
              <AggregationSelector
                selectedMethod={aggregations[0]!}
                aggregationMethods={aggregationMethods}
                onSelect={(method) => {
                  handleAggregationChange(method as AggregationMethod)
                }}
              />
              {aggregations.length > 1 &&
                aggregations[0] !== AggregationMethod.ProductName &&
                aggregations[0] !== AggregationMethod.Variant && (
                  <AggregationSelector
                    title="and "
                    prefix=""
                    selectedMethod={aggregations[aggregations.length - 1]!}
                    aggregationMethods={[AggregationMethod.ProductName, AggregationMethod.Variant]}
                    onSelect={(method) => {
                      handleAggregationChange(method as AggregationMethod, 1)
                    }}
                  />
                )}
            </div>
            <div className="flex items-center gap-2">
              <CustomerColumnChooser
                availableColumnIds={availableCustomerColumnIds}
                displayedColumnIds={displayedColumnIds}
                setDisplayedColumnIds={setDisplayedColumnIds}
              />
              <ExpandableSearchField
                initialSearchText={searchText}
                onSearchTextChange={setSearchText}
                placeholder="Search customers, products..."
              />
            </div>
          </div>
        )}
        <DataTableBaseV2<CustomerData>
          id={tableId}
          gridRef={gridRef}
          gridOptions={gridOptions}
          rowData={filteredCustomerData as CustomerData[]}
          loading={isLoading}
          columnDefs={newColumns}
          grandTotalRow="pinnedBottom"
          autoGroupColumnDef={autoGroupColumnDef}
          emptyMessage={searchText ? "No results found" : "No customer data available"}
          groupHideParentOfSingleChild={false}
          onDragStopped={handleDragStopped}
          rowSelection={ROW_SELECTION}
          onSelectionChanged={handleSelectionChangedLocal}
        />
      </div>
      <CustomerTagsCommandBar selectedVariantIds={selectedVariantIds} onClear={handleClear} />
    </>
  )
}

function CustomerTagsCommandBar({
  selectedVariantIds,
  onClear,
}: {
  selectedVariantIds: string[]
  onClear: () => void
}) {
  if (selectedVariantIds.length === 0) return null

  return (
    <Commandbar count={selectedVariantIds.length} onClear={onClear}>
      <EditSkuTagsDialog variantIds={selectedVariantIds} />
    </Commandbar>
  )
}
